import { useState, useEffect } from 'react';
import api from '../services/api';

export default function RevenuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const result = await api.getRevenue();
        setData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading revenue: {error}
        </div>
      </div>
    );
  }

  const r = data?.revenue || {};
  const tier = r.tier_distribution || {};
  const totalUsers = (tier.free || 0) + (tier.pro || 0) + (tier.ultra || 0);

  // Trial conversion rate
  const trialTotal = (r.trial_conversions_total || 0) + (r.trial_expired_total || 0);
  const trialRate = trialTotal > 0 ? Math.round((r.trial_conversions_total / trialTotal) * 1000) / 10 : 0;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-teacher-text-primary">Revenue</h1>
        <p className="text-teacher-text-secondary">Tier distribution, MRR, and conversion metrics · {data?.metricsDate || ''}</p>
      </div>

      {/* Tier distribution */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-teacher-border">
          <h3 className="text-xs font-medium text-teacher-text-secondary mb-1">Free</h3>
          <span className="text-2xl font-bold text-teacher-text-primary">{loading ? '...' : tier.free || 0}</span>
          {totalUsers > 0 && <p className="text-xs text-teacher-text-tertiary mt-1">{Math.round((tier.free || 0) / totalUsers * 100)}% of users</p>}
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-teacher-border border-l-4 border-l-blue-500">
          <h3 className="text-xs font-medium text-teacher-text-secondary mb-1">Pro</h3>
          <span className="text-2xl font-bold text-teacher-text-primary">{loading ? '...' : tier.pro || 0}</span>
          <p className="text-xs text-teacher-text-tertiary mt-1">Rs 199/mo</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-teacher-border border-l-4 border-l-purple-500">
          <h3 className="text-xs font-medium text-teacher-text-secondary mb-1">Ultra</h3>
          <span className="text-2xl font-bold text-teacher-text-primary">{loading ? '...' : tier.ultra || 0}</span>
          <p className="text-xs text-teacher-text-tertiary mt-1">Rs 399/mo</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-teacher-border">
          <h3 className="text-xs font-medium text-teacher-text-secondary mb-1">Active Trials</h3>
          <span className="text-2xl font-bold text-teacher-text-primary">{loading ? '...' : r.active_trials || 0}</span>
        </div>
        <div className={`bg-white rounded-xl p-5 shadow-sm border border-teacher-border ${trialRate >= 5 ? 'border-l-4 border-l-green-500' : trialRate >= 3 ? 'border-l-4 border-l-amber-500' : 'border-l-4 border-l-red-500'}`}>
          <h3 className="text-xs font-medium text-teacher-text-secondary mb-1">Trial-to-Paid</h3>
          <span className="text-2xl font-bold text-teacher-text-primary">{loading ? '...' : `${trialRate}%`}</span>
          <p className="text-xs text-teacher-text-tertiary mt-1">Target: 5-10%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* MRR */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
          <h3 className="text-lg font-semibold text-teacher-text-primary mb-2">Monthly Recurring Revenue</h3>
          <div className="text-center py-6">
            <div className="text-5xl font-extrabold text-teacher-text-primary">
              {loading ? '...'
                : r.mrr === null || r.mrr === undefined ? '—'
                : `₹${r.mrr.toLocaleString('en-IN')}`}
            </div>
            {/* MRR comes from subscription documents now, spread across each
                plan's billing period. The old caption multiplied the TIER head
                counts by the monthly list price - and those counts include
                trial users, so the page would have read "₹0" above
                "(79 × ₹199) + (5 × ₹399)". */}
            <p className="text-sm text-teacher-text-secondary mt-2">
              {r.mrr_source === 'unavailable'
                ? 'Subscription data unavailable — not reported'
                : r.mrr_source === 'subscriptions'
                  ? 'From active paid subscriptions, per billing period'
                  /* No mrr_source means a row written before 2026-09-01, when
                     MRR was a head count times the monthly list price and
                     counted trial users as customers. Do not vouch for it. */
                  : 'Legacy figure — counted trial users as paying, not comparable'}
            </p>
          </div>
          <div className="border-t border-teacher-border pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-teacher-text-secondary">Paid Users</span>
              <span className="font-semibold text-teacher-text-primary">
                {r.paid_users === null || r.paid_users === undefined ? '—' : r.paid_users}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-teacher-text-secondary">Trial Conversions (total)</span>
              <span className="font-semibold text-teacher-text-primary">{r.trial_conversions_total || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-teacher-text-secondary">Trials Expired (total)</span>
              <span className="font-semibold text-teacher-text-primary">{r.trial_expired_total || 0}</span>
            </div>
          </div>
        </div>

        {/* Unit economics placeholder */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-teacher-text-primary">Unit Economics</h3>
            <span className="text-xs font-medium px-2 py-1 rounded bg-teacher-bg-secondary text-teacher-text-secondary">Sprint 4</span>
          </div>
          <p className="text-sm text-teacher-text-secondary mb-4">
            Requires manual entry of API costs in <code className="text-xs bg-teacher-bg-secondary px-1 rounded">business_inputs</code> collection.
          </p>
          <div className="space-y-3">
            <div className="flex justify-between text-sm border-b border-gray-50 pb-2">
              <span className="text-teacher-text-secondary">Cost per Snap</span>
              <span className="text-teacher-text-tertiary">—</span>
              <span className="text-xs text-teacher-text-tertiary">Target: &lt; ₹2</span>
            </div>
            <div className="flex justify-between text-sm border-b border-gray-50 pb-2">
              <span className="text-teacher-text-secondary">Cost per Tutor Msg</span>
              <span className="text-teacher-text-tertiary">—</span>
              <span className="text-xs text-teacher-text-tertiary">Target: &lt; ₹1</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-teacher-text-secondary">Atlas Loop Cost</span>
              <span className="font-semibold text-green-600">₹0</span>
              <span className="text-xs text-teacher-text-tertiary">No LLM calls</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-teacher-border">
            <h4 className="text-sm font-semibold text-teacher-text-primary mb-3">Narrative Metrics</h4>
            <span className="inline-block text-xs px-2 py-0.5 rounded bg-teacher-bg-secondary text-teacher-text-secondary mb-3">Modeled / derived</span>
            <div className="space-y-2 text-sm text-teacher-text-tertiary">
              <div className="flex justify-between"><span>ARPU</span><span>—</span></div>
              <div className="flex justify-between"><span>Projected LTV</span><span>—</span></div>
              <div className="flex justify-between"><span>CAC</span><span>—</span></div>
              <div className="flex justify-between"><span>Payback Period</span><span>—</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
